import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { GiFairyWings } from 'react-icons/gi';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import './Expense.css';

const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const Expense = () => {
  const [expenses, setExpenses] = useState([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Expand view tracking
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try {
      const res = await axiosInstance.get('/api/expenses');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      
      // PUBLIC VIEW: Filter out deleted records AND disabled (inactive) records
      setExpenses(data.filter(e => !e.isDeleted && e.isActive !== false));
    } catch (error) {
      console.error("Failed to fetch expenses", error);
    }
  };

  // Extract unique years from the expense dates (Descending)
  const availableYears = useMemo(() => {
    const years = expenses
      .map(e => e.date ? new Date(e.date).getFullYear().toString() : null)
      .filter(Boolean);
    return [...new Set(years)].sort((a, b) => b - a);
  }, [expenses]);

  // Apply Filters
  const filteredData = useMemo(() => {
    let list = expenses;

    // Filter by Year
    if (yearFilter) {
      list = list.filter(e => e.date && new Date(e.date).getFullYear().toString() === yearFilter);
    }

    // Filter by Search (Particulars)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e => e.particular && e.particular.toLowerCase().includes(q));
    }

    // Sort by Date Descending
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [expenses, searchQuery, yearFilter]);

  // Pagination Logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / limit) || 1;
  const startRecord = (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, totalItems);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredData.slice(start, start + limit);
  }, [filteredData, currentPage, limit]);

  // Total amount calculation
  const totalAmount = useMemo(() => {
    return filteredData.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  }, [filteredData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  return (
    <>
      <Helmet>
        <title>Expense Records | Rajput Shiksha Kosh</title>
        <meta
          name="description"
          content="View and manage expense records for Rajput Shiksha Kosh."
        />
      </Helmet>
    <section className="view-expense-wrapper">
      <div className="view-expense-container">
        
        {/* HEADER */}
        <div className="view-expense-header">
          <h2 className="view-expense-title">EXPENSE RECORDS</h2>
          <div className="view-expense-divider">
            <span className="view-expense-divider-line"></span>
            <GiFairyWings className="view-expense-divider-icon" />
            <span className="view-expense-divider-line"></span>
          </div>
        </div>

        {/* TOP FILTER BAR */}
        <div className="view-expense-top-bar">
          <div className="view-expense-page-size">
            <label>Show</label>
            <select 
              className="view-expense-filter-select"
              value={limit} 
              onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
            >
              {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <label>entries</label>
          </div>

          <div className="view-expense-filters">
            <div className="view-expense-search-box">
              <FaSearch className="view-expense-icon-search" />
              <input 
                type="text" 
                placeholder="Search Particulars..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
              />
            </div>

            <select 
              className="view-expense-filter-select" 
              value={yearFilter} 
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="">Filter by Year (All)</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* TABLE COMPONENT */}
        <div className="view-expense-table-main-container">
          <div className="view-expense-table-responsive">
            <table className="view-expense-table">
              <thead>
                <tr>
                  <th className="view-expense-col-sno">S.No</th>
                  <th className="view-expense-col-date">Date</th>
                  <th className="view-expense-col-particular view-expense-align-left">Particular</th>
                  <th className="view-expense-col-amount">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr><td colSpan="4" className="view-expense-no-data">No expenses match your search criteria.</td></tr>
                ) : paginatedData.map((row, i) => (
                  <React.Fragment key={row._id}>
                    {/* MAIN ROW */}
                    <tr className="view-expense-table-row">
                      <td className="view-expense-col-sno">{startRecord + i}</td>
                      
                      {/* Date Col (Hidden < 768px via CSS) */}
                      <td className="view-expense-col-date">{new Date(row.date).toLocaleDateString('en-IN')}</td>
                      
                      {/* Particular Col WITH Expand Button */}
                      <td className="view-expense-col-particular view-expense-align-left">
                        <div className="view-expense-particular-wrapper" onClick={() => toggleRow(row._id)}>
                          <span className="font-weight-bold">{row.particular}</span>
                          <button className="view-expense-expand-trigger">
                            {expandedRow === row._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>
                      
                      {/* Amount Col (Right aligned via CSS) */}
                      <td className="view-expense-col-amount font-weight-bold" style={{color: '#e74c3c'}}>
                        ₹{Number(row.amount).toLocaleString('en-IN')}
                      </td>
                    </tr>

                    {/* EXPANDED ROW (For Mobile / Small Devices) */}
                    {expandedRow === row._id && (
                      <tr className="view-expense-expanded-row">
                        <td colSpan="4">
                          <div className="view-expense-expanded-content">
                            <div className="view-expense-expanded-grid">
                              <div className="view-expense-expanded-detail exp-date">
                                <span className="view-expense-expanded-label">Date</span>
                                <span className="view-expense-expanded-value">{new Date(row.date).toLocaleDateString('en-IN')}</span>
                              </div>
                              <div className="view-expense-expanded-detail exp-amount">
                                <span className="view-expense-expanded-label">Amount</span>
                                <span className="view-expense-expanded-value" style={{color: '#e74c3c', fontWeight: 'bold'}}>
                                  ₹{Number(row.amount).toLocaleString('en-IN')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>

              {/* TABLE FOOTER FOR TOTAL AMOUNT */}
              <tfoot>
                <tr className="view-expense-table-footer-row">
                  <td colSpan="4" className="view-expense-total-cell">
                    <div className="view-expense-total-container">
                      <span className="view-expense-total-label">
                        {yearFilter ? `Total Expense in ${yearFilter}:` : 'Total Expense (All Years):'}
                      </span>
                      <span className="view-expense-total-value">₹{totalAmount.toLocaleString('en-IN')}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* BOTTOM PAGINATION CONTROLS */}
        <div className="view-expense-bottom-bar">
          <div className="view-expense-pagination-info">
            Showing {totalItems === 0 ? 0 : startRecord} to {endRecord} of {totalItems} entries
          </div>

          <div className="view-expense-pagination-controls">
            <button 
              className="view-expense-page-btn" 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button 
              className="view-expense-page-btn" 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>

      </div>
    </section>
    </>
  );
};

export default Expense;